import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ResearcherAccount from "../../components/settings/pages/researcher/ResearcherAccount";
import ResearcherSecurity from "../../components/settings/pages/researcher/ResearcherSecurity";
import CertificatesManager from "../../components/settings/pages/researcher/CertificatesManager";
import NotificationSettings from "../../components/settings/NotificationSettings";
import NotificationInfoCard from "../../components/settings/NotificationInfoCard";
import HistoryOfScopes from "../../components/settings/pages/HistoryOfScopes";
import { getOrgResponses } from "../../utils/api/endpoints/questionnaire";

type Tab = "account" | "notifications" | "security" | "info" | "scopes-history";

const MenuIcon = ({ name, active }: { name: Tab; active: boolean }) => {
  const color = active ? "#58CC02" : "#6B7280"; // green-700 or gray-500

  if (name === "account")
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    );

  if (name === "security")
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    );

  if (name === "notifications")
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
    );

  if (name === "scopes-history")
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    );

  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
};

const MenuButton = ({
  active,
  label,
  onClick,
  icon
}: {
  active: boolean;
  label: string;
  onClick: () => void;
  icon: Tab;
}) => (
  <button
    className={`w-full text-left px-3 py-2 rounded-md transition-colors flex items-center gap-3 ${active ? "bg-[#58CC02]/[0.17] text-[#58CC02]" : "text-gray-700 hover:bg-gray-50"
      }`}
    onClick={onClick}
  >
    <MenuIcon name={icon} active={active} />
    <span className="text-sm font-medium">{label}</span>
  </button>
);

const ResearcherSettingsPage = () => {
  const [tab, setTab] = useState<Tab>("account");
  const [hasScopes, setHasScopes] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    getOrgResponses()
      .then((responses) => {
        if (Array.isArray(responses) && responses.length > 0) {
          setHasScopes(true);
        } else {
          setHasScopes(false);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch responses in settings side nav", err);
        setHasScopes(false);
      });

    // Parse tab query parameter from URL (e.g. ?tab=security)
    const params = new URLSearchParams(window.location.search);
    const queryTab = params.get("tab") as Tab;
    if (queryTab && ["account", "notifications", "security", "info", "scopes-history"].includes(queryTab)) {
      setTab(queryTab);
    }
  }, []);

  const goBack = () => navigate("/dashboard/profile");

  return (
    <div className="p-6 bg-gray-100 min-h-screen" style={{ fontFamily: 'Montserrat, sans-serif' }}>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
          <p className="text-gray-600 mt-1">Manage your personal information and security researcher profile</p>
        </div>
        <button
          onClick={goBack}
          className="text-blue-600 text-sm hover:underline"
        >
          Back to Profile
        </button>
      </div>

      <section className="w-full grid grid-cols-1 md:grid-cols-[280px,1fr] gap-6 items-start">
        {/* Left Settings Menu */}
        <div className="rounded-lg bg-white p-4 shadow-sm border border-gray-200 sticky top-6 h-max">
          <div className="text-sm font-semibold text-gray-700 mb-4">Settings Menu</div>
          <div className="space-y-2">
            <MenuButton
              active={tab === "account"}
              label="Account"
              onClick={() => setTab("account")}
              icon="account"
            />
            <MenuButton
              active={tab === "security"}
              label="Security"
              onClick={() => setTab("security")}
              icon="security"
            />
            <MenuButton
              active={tab === "notifications"}
              label="Notifications"
              onClick={() => setTab("notifications")}
              icon="notifications"
            />
            <MenuButton
              active={tab === "info"}
              label="About Information"
              onClick={() => setTab("info")}
              icon="info"
            />
            {hasScopes && (
              <MenuButton
                active={tab === "scopes-history"}
                label="History of Scopes"
                onClick={() => setTab("scopes-history")}
                icon="scopes-history"
              />
            )}
          </div>
          {tab === "notifications" && (
            <NotificationInfoCard />
          )}
        </div>

        {/* Right Content Container */}
        <section className={`min-w-0 ${tab === "notifications" ? "space-y-6" : "rounded-lg bg-white p-6 shadow-sm border border-gray-200"}`}>
          {tab === "account" && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Account Settings</h2>
              <p className="text-sm text-gray-500 mb-6">Manage your personal information and security researcher profile</p>
              <ResearcherAccount />
            </div>
          )}

          {tab === "security" && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Security Settings</h2>
              <p className="text-sm text-gray-500 mb-6">Change your password and manage two-factor authentication</p>
              <ResearcherSecurity />
            </div>
          )}

          {tab === "notifications" && (
            <div>
              <NotificationSettings />
            </div>
          )}

          {tab === "info" && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Account Information</h2>
              <p className="text-sm text-gray-500 mb-6">Add and manage your certifications</p>
              <CertificatesManager />
            </div>
          )}

          {tab === "scopes-history" && (
            <HistoryOfScopes />
          )}
        </section>
      </section>
    </div>
  );
};

export default ResearcherSettingsPage;


